import { Client } from "@/Common/Api/Client";
import { ClientResponse } from "@/Common/Api/ClientResponse";
import { GenericResponse } from "@/Common/Api/Dto/GenericResponse";
import { Guid } from "@/Common/Models/Guid";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { CreateCustomDocumentClassResponse } from "./CreateCustomDocumentClassResponse";
import { GetConfidentialityStatementResponse } from "./GetConfidentialityStatementResponse";
import { GetRawMetadataResponse } from "./GetRawMetadataResponse";
import { OrphanDocumentsCountResponse } from "./OrphanDocumentsCountResponse";
import { OrphanDocumentsResponse } from "./OrphanDocumentsResponse";
import { UpdateConfidentialityStatementResponse } from "./UpdateConfidentialityStatementResponse";
import { UserNotificationsResponse } from "./UserNotificationsResponse";

class OntarioMdHealthReportManagerApiClient {
    /**
     * Triggers a pull of Health Reports from the Ontario MD Health Report Manager backend service.
     * Returns a GenericResponse (no additional payload expected on success currently).
     */
    public static async triggerPull(): Promise<ClientResponse<GenericResponse>> {
        const response = await Client.postAsync<GenericResponse>(ServerOperations.Server.Myle_OntarioMdHealthReportManagerModule_TriggerPull, {});

        return response;
    }

    /**
     * Queries unlinked orphan documents from the Ontario MD Health Report Manager.
     * Returns a list of orphan documents that need to be matched to patients.
     */
    public static async queryOrphanDocuments(): Promise<ClientResponse<OrphanDocumentsResponse>> {
        const response = await Client.postAsync<OrphanDocumentsResponse>(ServerOperations.Server.Myle_OntarioMdHealthReportManagerModule_QueryOrphanDocuments, {});

        return response;
    }

    /**
     * Retrieves the total count of orphan documents currently requiring attention.
     */
    public static async getOrphanDocumentsCount(): Promise<ClientResponse<OrphanDocumentsCountResponse>> {
        const response = await Client.postAsync<OrphanDocumentsCountResponse>(ServerOperations.Server.Myle_OntarioMdHealthReportManagerModule_GetOrphanDocumentsCount, {});

        return response;
    }

    /**
     * Links an orphan record with a Myle patient.
     * Returns a GenericResponse (no additional payload expected on success currently).
     */
    public static async linkPatient(orphanDocumentId: Guid, patientId: Guid): Promise<ClientResponse<GenericResponse>> {
        const response = await Client.postAsync<GenericResponse>(ServerOperations.Server.Myle_OntarioMdHealthReportManagerModule_LinkPatient, {
            OrphanDocumentId: orphanDocumentId,
            PatientContactFacetId: patientId
        });

        return response;
    }

    /**
     * Gets the raw metadata for given document attachment(s).
     * Returns a list of raw metadata items.
     */
    public static async getRawMetadata(id: Guid): Promise<ClientResponse<GetRawMetadataResponse>> {
        const response = await Client.postAsync<GetRawMetadataResponse>(ServerOperations.Server.Myle_OntarioMdHealthReportManagerModule_GetRawMetadata, {
            Id: id
        });
        return response;
    }

    /**
     * Gets the current confidentiality statement and interval configuration.
     * Returns both the confidentiality statement and interval configuration.
     */
    public static async getConfidentialityStatement(): Promise<ClientResponse<GetConfidentialityStatementResponse>> {
        const response = await Client.postAsync<GetConfidentialityStatementResponse>(ServerOperations.Server.Myle_OntarioMdHealthReportManagerModule_GetConfidentialityStatement, {});

        return response;
    }

    /**
     * Updates both the confidentiality statement and interval configuration.
     * Returns the update result.
     */
    public static async updateConfidentialityStatement(confidentialityStatement: string, interval?: string): Promise<ClientResponse<UpdateConfidentialityStatementResponse>> {
        const response = await Client.postAsync<UpdateConfidentialityStatementResponse>(ServerOperations.Server.Myle_OntarioMdHealthReportManagerModule_UpdateConfidentialityStatement, {
            confidentialityStatement,
            interval
        });

        return response;
    }

    /**
     * Fetches user notifications related to Health Report Manager failures.
     * Returns a list of notifications for the user.
     */
    public static getUserNotifications(): Promise<ClientResponse<UserNotificationsResponse>> {
        return Client.postAsync<UserNotificationsResponse>(ServerOperations.Server.Myle_OntarioMdHealthReportManagerModule_GetNotifications, {});
    }

    /**
     * Hides a specific notification for the user.
     * Returns a GenericResponse (no additional payload expected on success).
     * @param failureId The ID of the failure notification to hide.
     */
    public static hideNotification(failureId: string): Promise<ClientResponse<GenericResponse>> {
        return Client.postAsync<GenericResponse>(ServerOperations.Server.Myle_OntarioMdHealthReportManagerModule_HideNotification, {
            FailureId: failureId
        });
    }

    /**
     * Creates a custom document class and/or subclass.
     * Returns the created lookup IDs for the new custom classes.
     * @param customSubClass The custom subclass name to create (optional).
     */
    public static createCustomDocumentClass(customClass: string, customSubClass?: string): Promise<ClientResponse<CreateCustomDocumentClassResponse>> {
        return Client.postAsync<CreateCustomDocumentClassResponse>(ServerOperations.Server.Myle_OntarioMdHealthReportManagerModule_CreateCustomDocumentClass, {
            CustomClass: customClass,
            CustomSubClass: customSubClass || null
        });
    }
}

export { OntarioMdHealthReportManagerApiClient };
